import React, { useState, useEffect, useRef } from 'react';

/**
 * SuggestionCard component displays clickable suggestion cards in the chat interface
 * Cards can be clicked to send a predefined message and they animate on hover
 */
const SuggestionCard = ({ text, onClick, visible = true, isTutorialActive = false }) => {
  const [isVisible, setIsVisible] = useState(visible);
  
  // Handle visibility changes from props
  useEffect(() => {
    setIsVisible(visible);
  }, [visible]);
  
  const handleClick = (e) => {
    // If tutorial is active, prevent click
    if (isTutorialActive) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    // Hide the card when clicked
    setIsVisible(false);
    
    // Call the parent onClick handler
    if (onClick) {
      onClick(text);
    }
  };
  
  return (
    <div 
      className={`suggestion-card ${isVisible ? 'suggestion-card--visible' : 'suggestion-card--hidden'} ${isTutorialActive ? 'suggestion-card--tutorial' : ''}`}
      onClick={handleClick}
    >
      <div className="suggestion-card__content">
        {text}
      </div>
    </div>
  );
};

/**
 * SuggestionCards component displays a row of clickable suggestion cards
 * This container manages multiple suggestion cards and their visibility
 * Always reserves space in the chat interface for a smooth user experience
 */
const SuggestionCards = ({ suggestions = [], onSuggestionClick, visible = false, isTutorialActive = false }) => {
  const [isVisible, setIsVisible] = useState(visible);
  const [displayedSuggestions, setDisplayedSuggestions] = useState(suggestions);
  const prevVisibleRef = useRef(visible);
  const prevSuggestionsRef = useRef(suggestions);
  
  // Handle suggestion content changes
  useEffect(() => {
    const prevVisible = prevVisibleRef.current;
    const suggestionsChanged = JSON.stringify(prevSuggestionsRef.current) !== JSON.stringify(suggestions);
    
    // If the suggestions have changed and the component was previously visible
    // we need to handle the transition carefully
    if (suggestionsChanged) {
      if (prevVisible && !visible) {
        // If we're transitioning from visible to hidden, keep old suggestions until hidden
        prevSuggestionsRef.current = suggestions;
      } else if (!prevVisible && visible) {
        // If we're transitioning from hidden to visible, use new suggestions
        setDisplayedSuggestions(suggestions);
        prevSuggestionsRef.current = suggestions;
      } else if (visible) {
        // If we remain visible but suggestions changed, we need to update
        setDisplayedSuggestions(suggestions);
        prevSuggestionsRef.current = suggestions;
      } else {
        // If we remain hidden but suggestions changed, just update the ref
        prevSuggestionsRef.current = suggestions;
      }
    }
  }, [suggestions, visible]);
  
  // Update visibility based on prop changes
  useEffect(() => {
    // Update local state to match prop
    setIsVisible(visible);
    
    // If becoming visible, ensure we have the latest suggestions
    if (visible && !prevVisibleRef.current) {
      setDisplayedSuggestions(suggestions);
    }
    
    // Update our ref
    prevVisibleRef.current = visible;
  }, [visible, suggestions]);
  
  const handleCardClick = (text) => {
    // If tutorial is active, don't process clicks
    if (isTutorialActive) {
      return;
    }
    
    // Hide all cards when one is clicked
    setIsVisible(false);
    
    // Call the parent handler with the selected suggestion
    if (onSuggestionClick) {
      onSuggestionClick(text);
    }
  };
  
  // If no suggestions, don't render anything
  if (displayedSuggestions.length === 0 && !isVisible) {
    return null;
  }
  
  // If not visible, render a placeholder to reserve space
  if (!isVisible) {
    return <div className="suggestion-cards__placeholder" aria-hidden="true"></div>;
  }
  
  return (
    <div className="suggestion-cards">
      <div className="suggestion-cards__container">
        {displayedSuggestions.map((suggestion, index) => (
          <SuggestionCard
            key={`suggestion-${index}-${suggestion}`}
            text={suggestion}
            onClick={handleCardClick}
            visible={isVisible}
            isTutorialActive={isTutorialActive}
          />
        ))}
      </div>
    </div>
  );
};

export default SuggestionCards; 